import React from 'react';
import { Eye, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { NotificationStatus } from './NotificationStatus';
import { PreRegistrationStatusBadge } from './PreRegistrationStatusBadge';
import { PreRegistrationRowActions } from './PreRegistrationRowActions';
import { NO_LOCALITY_LABEL, formatOptionalCount } from '../utils/format.utils';
import { isRowDataEditable, isRowDeleted } from '../utils/status-transitions.utils';
import type { MunicipalListAccommodation } from '../types/pre-registration.types';

interface MunicipalListAccommodationsTableProps {
  rows: MunicipalListAccommodation[];
  canEdit: boolean;
  // Department of the list's municipality, for the REPAT lookup of the status actions
  departmentId?: number;
  onView: (row: MunicipalListAccommodation) => void;
  onEdit: (row: MunicipalListAccommodation) => void;
}

export const MunicipalListAccommodationsTable: React.FC<MunicipalListAccommodationsTableProps> = ({
  rows,
  canEdit,
  departmentId,
  onView,
  onEdit,
}) => (
  <Table className="border border-gray-200">
    <TableHeader className="bg-gray-100">
      <TableRow>
        <TableHead className="w-10">#</TableHead>
        <TableHead>Denominación</TableHead>
        <TableHead>Localidad</TableHead>
        <TableHead>Referente</TableHead>
        <TableHead className="text-right">Plazas</TableHead>
        <TableHead className="text-right">Habitaciones</TableHead>
        <TableHead>Estado</TableHead>
        <TableHead>Fiscalización</TableHead>
        <TableHead className="text-right">Acciones</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {rows.map((row, index) => (
        <TableRow
          key={row.id}
          className={`cursor-pointer ${isRowDeleted(row) ? 'text-gray-400' : ''}`}
          onClick={() => onView(row)}
        >
          <TableCell className="text-gray-500">{index + 1}</TableCell>
          <TableCell className="font-medium">{row.establishmentName}</TableCell>
          <TableCell className={row.locality ? undefined : 'text-gray-500'}>
            {row.locality?.name ?? NO_LOCALITY_LABEL}
          </TableCell>
          <TableCell>
            <div>{row.contactName ?? '—'}</div>
            {row.contactPhone && <div className="text-xs text-gray-500">{row.contactPhone}</div>}
          </TableCell>
          <TableCell className="text-right">{formatOptionalCount(row.places)}</TableCell>
          <TableCell className="text-right">{formatOptionalCount(row.rooms)}</TableCell>
          <TableCell>
            <PreRegistrationStatusBadge status={row.status} />
          </TableCell>
          <TableCell>
            <NotificationStatus row={row} />
          </TableCell>
          {/* Buttons must not open the row */}
          <TableCell className="text-right" onClick={(event) => event.stopPropagation()}>
            <div className="flex justify-end gap-1">
              <Button variant="ghost" size="icon" title="Ver" onClick={() => onView(row)}>
                <Eye className="h-4 w-4" />
              </Button>
              {canEdit && isRowDataEditable(row.status.code) && (
                <Button variant="ghost" size="icon" title="Editar" onClick={() => onEdit(row)}>
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              {canEdit && <PreRegistrationRowActions row={row} departmentId={departmentId} />}
            </div>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);
